import { ParsedUrlQuery } from 'node:querystring'

export const isIOSDevice = (): boolean => {
  // iOS 12 and below has a quirk that does not exist in Mac OS. The quirk is that the volume property of an Audio element cannot be successfully set to any value other than 1. This is because Apple does not allow volume changes on the Audio element for iOS devices, but does for Mac OS. That quirk can be used as the final fallback method for distinguishing an iOS device from a Mac OS device
  const iosQuirkPresent = () => {
    const audio = new Audio()
    audio.volume = 0.5
    return audio.volume === 1 // volume cannot be changed from "1" on iOS 12 and below
  }
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
  const isAppleDevice = navigator.userAgent.includes('Macintosh')
  const isTouchScreen = navigator.maxTouchPoints >= 1 // true for iOS 13 (and hopefully beyond)
  return isIOS || (isAppleDevice && (isTouchScreen || iosQuirkPresent()))
}

// Note - the below function does not catch iOS 13 onwards where the iPad useragent is now different - so iPad/iPad Air/iPad Pro are affected and not caught in the below if after iOS 13
export const isMobileOrTabletDevice = (): boolean => /Android|webOS|iPhone|iPad|iPod|IEMobile|Opera Mini/i.test(navigator.userAgent)

export const isAutotraderApp = (query: ParsedUrlQuery): boolean => {
  const apps = ['ios-iphone-app', 'android-phone-app', 'ios-ipad-app', 'android-tablet-app']
  return apps.some((app:string) => query.device === app)
}

const hasMultipleCampaigns = (campaign: string | string[]): boolean => Array.isArray(campaign)

const hasAppCampaign = (campaign: string | string[]): boolean => {
  if (hasMultipleCampaigns(campaign)) {
    return (campaign as string[]).some((campaign) => typeof campaign === 'string' && campaign.startsWith('App'))
  }
  return !!campaign && typeof campaign === 'string' && campaign.startsWith('App')
}

export const isAutotraderAppCampaign = (query: ParsedUrlQuery): boolean => {
  const { utm_campaign, utm_source } = query
  if (utm_source !== 'autotrader' || utm_campaign === undefined) return false

  return hasAppCampaign(utm_campaign)
}

// Check if the source is Autotrader and the device type is Android
export const isAutotraderAppCampaignForAndroid = (query: ParsedUrlQuery): boolean => {
  // Autotrader passes device=android-phone-app in the query to us, which is what we're using to distinguish it here - https://kibana.zuto.com/app/r/s/Q631C
  const { utm_campaign, utm_source, device } = query
  
  const isNotAutotrader = utm_source !== 'autotrader'
  const isNotAndroid = !device || !device.includes('android')
  const utmCampaignNotPresent = utm_campaign === undefined

  if (isNotAutotrader || isNotAndroid || utmCampaignNotPresent) return false

  return hasAppCampaign(utm_campaign)
}
